import React, { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import WorkspaceLayout from "../components/WorkspaceLayout";
import B2BLayout from "../components/b2b/B2BLayout";
import { useAppContext } from "../context/AppContext";
import {
  sendConversationMessage,
  conversationMakerFinalizeOneshot,
} from "../services/api";
import { generateStudyName, generateProjectName } from "../services/api";
import { apiForm } from "../services/apiClient";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner, faPaperPlane, faRobot, faTimes, faPaperclip, faChevronDown, faFile } from "@fortawesome/free-solid-svg-icons";
import ReactMarkdown from "react-markdown";
import { Button } from "../components/ui";
import sparkleIcon from "../assets/icons/special/ico_sparkle.svg";
import "./ConversationStudyMakerPage.scss";
import { usePageHeader } from "../context/PageHeaderContext";
import { useB2BMode } from "../hooks/useB2BMode";

const STEPS = [
  { id: "context", title: "상황/컨텍스트" },
  { id: "rq", title: "목적/질문/가설" },
  { id: "method", title: "방법론/내용 설계" },
  { id: "audience", title: "대상/스크리너" },
  { id: "additional", title: "추가 요구사항" },
];

const STEP_START_PROMPTS = [
  [
    "1단계 : 상황/컨텍스트",
    "",
    "이번 리서치를 시작하게 된 배경이나 상황을 자유롭게 알려주세요.",
    "",
    "예를 들어:",
    "- 해결하거나 검증하고 싶은 문제나 이슈",
    "- 최근 변화나 징후 (CS 피드백, 데이터 분석 결과, 서비스 변화 등)",
    "- 리서치에 포함하거나 제외해야 할 범위나 제약사항",
    "",
    "위 예시에 국한되지 않고, 리서치를 시작하게 된 경위나 배경을 편하게 작성해주시면 됩니다.",
  ].join("\n"),
  [
    "2단계 · 목적/질문/가설",
    "",
    "이번 리서치의 목적, 연구 질문, 또는 가설을 자유롭게 알려주세요.",
    "",
    "예를 들어:",
    "- 이번 조사로 내릴 '의사결정'은 무엇인가요?",
    "- 꼭 확인해야 할 리서치 질문(RQ)은 무엇인가요?",
    "- 지금 가진 가설/가정이 있다면 편하게 적어주세요. (완성형이 아니어도 괜찮아요)",
    "",
    "위 예시에 국한되지 않고, 리서치의 목적이나 가설을 편하게 작성해주시면 됩니다.",
  ].join("\n"),
  [
    "3단계 · 방법론/설계",
    "",
    "이번 리서치의 방법론이나 설계에 대한 정보를 자유롭게 알려주세요.",
    "",
    "예를 들어:",
    "- 시간/리소스 제약(기간/인원/리서처 수)은 어떻게 되나요?",
    "- 정성/정량 중 우선순위는 무엇인가요? 왜 그런가요?",
    "- 원격/대면, 과업 기반/자유 탐색 중 선호가 있으신가요?",
    "",
    "위 예시에 국한되지 않고, 리서치 방법론이나 설계에 대한 정보를 편하게 작성해주시면 됩니다.",
  ].join("\n"),
  [
    "4단계 · 대상/쿼터/스크리너",
    "",
    "이번 리서치의 조사 대상이나 스크리너 기준을 자유롭게 알려주세요.",
    "",
    "예를 들어:",
    "- 핵심 타깃은 누구인가요? (경험 유무/빈도/기간 등으로)",
    "- 비교군이 필요한가요? (예: 초보 vs 헤비)",
    "- 반드시 제외해야 하는 조건이 있나요? (업계 종사자, 내부 직원 등)",
    "- 모집 시 균형을 맞추고 싶은 기준이 있나요? (플랫폼/숙련도/사용 빈도 등)",
    "",
    "위 예시에 국한되지 않고, 조사 대상이나 스크리너 기준을 편하게 작성해주시면 됩니다.",
  ].join("\n"),
  [
    "5단계 · 추가 요구사항",
    "",
    "지금까지 수집한 정보를 바탕으로, 리서치 설계를 더욱 구체화하기 위해 필요한 추가 정보를 자유롭게 알려주세요.",
    "",
    "예를 들어:",
    "- 검증할 핵심 시나리오/기능 (UT/IDI의 경우, task 설계를 위해)",
    "- 특정 화면이나 기능에 대한 집중 관찰 포인트",
    "- 편향을 제거하기 위해 고려해야 할 사항",
    "- 추가로 확인이 필요한 제약사항이나 조건",
    "",
    "위 예시에 국한되지 않고, 현재까지의 설계를 보완하기 위해 필요한 정보를 자유롭게 입력해주시면 됩니다.",
  ].join("\n"),
];

const makeRequestId = () => {
  const cryptoApi =
    typeof window !== "undefined" &&
    window.crypto &&
    typeof window.crypto.randomUUID === "function"
      ? window.crypto
      : undefined;
  if (cryptoApi) return cryptoApi.randomUUID();
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const hashString = (str) => {
  // 작은/가벼운 해시(비암호)로 콘텐츠 fingerprint 생성
  // eslint-disable-next-line no-bitwise
  let h = 2166136261;
  const s = String(str || "");
  for (let i = 0; i < s.length; i += 1) {
    // eslint-disable-next-line no-bitwise
    h ^= s.charCodeAt(i);
    // eslint-disable-next-line no-bitwise
    h = Math.imul(h, 16777619);
  }
  // eslint-disable-next-line no-bitwise
  return (h >>> 0).toString(16);
};

const normalizeCard = (raw, fallbackIndex = 0) => {
  const baseId =
    raw?.id ||
    raw?.card_id ||
    `card-${Date.now()}-${fallbackIndex}-${Math.random().toString(16).slice(2)}`;
  const fingerprint = hashString(
    `${raw?.type || ""}||${raw?.title || ""}||${raw?.content || ""}||${raw?.because || ""}`
  );
  const id = `${baseId}-${fingerprint}`;
  return {
    id,
    type: raw?.type || "note",
    // LLM이 content 끝에 개행(\n\n) 등을 붙여서 내려주는 경우가 있어
    // white-space: pre-wrap 렌더링에서 "카드 아래 큰 공백"으로 보일 수 있음 → 앞/뒤 공백 제거
    title: String(raw?.title || raw?.type || "카드").trim(),
    content: String(raw?.content || "").trim(),
    because: String(raw?.because || "").trim(),
    fields: raw?.fields || {},
    status: raw?.status || "proposed",
    tags: Array.isArray(raw?.tags) ? raw.tags : [],
  };
};

const isQuestionLikeCard = (card) => {
  const type = String(card?.type || "").toLowerCase();
  const title = String(card?.title || "").trim();
  const content = String(card?.content || "").trim();

  // 백엔드가 '질문'을 카드로 내려주는 케이스가 있음 → 선택(누적) 후보로 노출하면 UX가 깨짐
  if (type.includes("question")) return true; // research_question, decision_question, missing_info_question 등
  if (title.endsWith("?")) return true;
  if (content.endsWith("?")) return true;
  return false;
};

const STEP_SINGLE_QUESTION = [
  // 1단계에서 첫 추천 클릭 시 딱 1개만 묻는 질문
  "이번에 검증하고 싶은 화면이나 기능, 또는 시나리오가 있다면 알려주세요.",
  "이번 조사에서 가장 중요한 ‘의사결정’은 무엇인가요?",
  "시간/리소스 제약(기간/인원/리서처 수)은 어떻게 되나요?",
  "핵심 타깃(사용자)을 한 문장으로 정의하면 어떤 사람들인가요?",
  "검증할 핵심 시나리오 1~2개는 무엇인가요?",
];

const buildStudyNameSeed = ({ ledgerCards, messages }) => {
  const ledgerText = (ledgerCards || [])
    .slice(0, 6)
    .map((c) => `${c?.title ? `${c.title}: ` : ""}${c?.content || ""}`.trim())
    .filter(Boolean)
    .join("\n");

  const lastUserMsgs = (messages || [])
    .filter((m) => m?.type === "user" && m?.content)
    .slice(-3)
    .map((m) => String(m.content).trim())
    .filter(Boolean)
    .join("\n");

  const combined = [ledgerText, lastUserMsgs].filter(Boolean).join("\n\n");
  return combined || "리서치 계획을 생성합니다.";
};

export default function ConversationStudyMakerPage() {
  const navigate = useNavigate();
  const { projectSlug } = useParams();
  const { isB2BMode } = useB2BMode();
  const Layout = isB2BMode ? B2BLayout : WorkspaceLayout;
  const {
    getProject,
    loadProjects,
    workspaceLoaded,
    isLoggedIn,
    sessionChecked,
    canCreateStudy,
    canCreateProject,
    createProject: createProjectInContext,
  } = useAppContext();

  const project = projectSlug ? getProject(projectSlug) : null;
  const projectId = project?.id ?? null;

  useEffect(() => {
    if (!sessionChecked || !isLoggedIn) return;
    loadProjects();
  }, [isLoggedIn, loadProjects, sessionChecked]);

  // 헤더 설정 - 컴포넌트 상단에서 먼저 실행하여 헤더가 빠르게 표시되도록 함
  usePageHeader(
    {
      title: "새 리서치 생성",
      subtitle: project?.name || "",
      showBack: true,
      onBack: () => navigate(-1),
    },
    [project] // project 객체 전체를 dependency로 사용하여 변경 감지
  );

  const [stepIndex, setStepIndex] = useState(0);
  const step = STEPS[stepIndex];

  const [chatInput, setChatInput] = useState("");
  const [messages, setMessages] = useState([
    {
      type: "ai",
      content: STEP_START_PROMPTS[0],
    },
  ]);

  const [draftCards, setDraftCards] = useState([]);
  const [ledgerCards, setLedgerCards] = useState([]);
  const [planDraft, setPlanDraft] = useState(null); // 다음 단계로 넘어갈 때만 구조화된 프리뷰 갱신
  const [loading, setLoading] = useState(false);
  const [finalizeLoading, setFinalizeLoading] = useState(false); // 최종 생성 전용 로딩 상태
  const [isPageReady, setIsPageReady] = useState(false); // 페이지 초기 렌더링 완료 여부
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [inputAttachments, setInputAttachments] = useState([]);
  const isUploading = useMemo(
    () => inputAttachments.some((a) => a?.status === "uploading"),
    [inputAttachments]
  );
  const fileInputRef = useRef(null);
  const chatTextareaRef = useRef(null);
  const objectUrlsRef = useRef(new Set());
  const [hasAskedFirstQuestionByStep, setHasAskedFirstQuestionByStep] = useState(
    () => STEPS.map(() => false)
  );
  const [isComposing, setIsComposing] = useState(false); // 한글 입력 중인지 추적
  const [showNextStepHint, setShowNextStepHint] = useState(true); // 다음 단계 안내 말풍선 표시 여부
  const [expandedRecommendations, setExpandedRecommendations] = useState(new Set()); // 펼쳐진 추천 메시지 인덱스
  const [expandedSections, setExpandedSections] = useState(new Set()); // 펼쳐진 카테고리 섹션
  const [expandedMessageAttachments, setExpandedMessageAttachments] = useState(new Set()); // 펼쳐진 메시지 첨부파일 (message index)
  const [showScrollToBottom, setShowScrollToBottom] = useState(false); // 스크롤 아래로 가기 버튼 표시 여부

  const activeRequestIdRef = useRef(null);
  const chatEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const prevLoadingRef = useRef(false);
  const studyNameRef = useRef(""); // 최종 생성 시 사용할 리서치명 (내부적으로만 사용)
  const chatInputRef = useRef(null);
  const nextStepButtonRef = useRef(null); // 다음 단계 버튼 위치 계산용
  const messageRefs = useRef({}); // 메시지별 ref 저장
  const dividerRefs = useRef({}); // 단계 경계선별 ref 저장
  const pinnedDividerIndexRef = useRef(null); // 상단에 고정할 경계선 인덱스

  // 1단계에서 사용자 메시지가 있는지 확인
  const hasUserInputInStep0 = () => {
    const userMessages = messages.filter(m => m.type === 'user');
    return userMessages.length > 0;
  };

  // 각 단계별로 해당 단계의 카드 타입 정의
  const getStepCardTypes = (stepIdx) => {
    const stepCardTypeMap = {
      0: ['project_context', 'scope_item'], // 상황 단계
      1: ['research_goal', 'hypothesis'],    // 목적 단계
      2: ['methodology_set', 'methodology'], // 방법론 단계
      3: ['audience_segment', 'quota_plan', 'screener_rule'], // 대상 단계
      4: [], // 추가 요구사항 단계 - 모든 타입 허용 (AI가 판단)
    };
    return stepCardTypeMap[stepIdx] || [];
  };

  // 현재 단계에서 선택된 카드가 있는지 확인
  const hasStepCardSelected = (stepIdx) => {
    const requiredTypes = getStepCardTypes(stepIdx);
    // 4단계(추가 요구사항)는 모든 타입 허용이므로 빈 배열일 때는 카드가 하나라도 있으면 통과
    if (requiredTypes.length === 0) {
      return ledgerCards.length > 0;
    }
    return ledgerCards.some(card => 
      requiredTypes.some(type => 
        String(card?.type || '').toLowerCase().includes(type.toLowerCase())
      )
    );
  };

  // 다음 단계로 진행 가능한지 확인
  const canProceedToNextStep = () => {
    // 마지막 단계면 비활성화
    if (stepIndex >= STEPS.length - 1) return false;
    
    // 카드가 하나라도 있으면 활성화
    return ledgerCards.length > 0;
  };

  // ledger_cards에서 방법론 추출
  const getSelectedMethodologies = (cards) => {
    return cards
      .filter(card => {
        const type = String(card?.type || "").toLowerCase();
        return type.includes("methodology") || type.includes("method");
      })
      .map(card => (card?.content || card?.title || "").toLowerCase())
      .filter(Boolean);
  };




  const addMessage = (msg) => setMessages((prev) => [...prev, msg]);

  const SECTION_LABELS = {
    context: "상황/컨텍스트",
    rq: "목적/질문/가설",
    method: "방법론/내용 설계",
    audience: "대상/스크리너",
    extra: "추가 요구사항",
  };

  const buildPlanDraft = (cards) => {
    const by = { context: [], rq: [], method: [], audience: [], extra: [] };
    (cards || []).forEach((c) => {
      const t = String(c?.type || "").toLowerCase();
      if (t.includes("project_context") || t.includes("scope_item")) by.context.push(c);
      else if (t.includes("research_goal") || t.includes("hypothesis")) by.rq.push(c);
      else if (t.includes("methodology")) by.method.push(c);
      else if (t.includes("audience") || t.includes("quota") || t.includes("screener")) by.audience.push(c);
      else by.extra.push(c);
    });
    return {
      updatedAt: Date.now(),
      sourceIds: (cards || []).map((c) => c?.id).filter(Boolean).sort(),
      sections: Object.fromEntries(Object.entries(by).filter(([, arr]) => arr.length > 0)),
    };
  };

  const addToLedger = (card) => {
    let didAdd = false;
    setLedgerCards((prev) => {
      if (prev.some((c) => c.id === card.id)) return prev;
      didAdd = true;
      return [...prev, { ...card, status: "selected" }];
    });
  };

  const removeFromLedger = (cardId) => {
    setLedgerCards((prev) =>
      prev.filter((c) => c.id !== cardId)
    );
  
  };

  const resetDraft = () => setDraftCards([]);

  const addInputAttachment = (file, previewUrl = null) => {
    const id = `att-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setInputAttachments((prev) => [
      ...prev,
      {
        id,
        name: file?.name || "file",
        type: file?.type || "",
        previewUrl,
        status: "uploading",
        errorMessage: "",
        fileUrl: "",
      },
    ]);
    return id;
  };

  const updateInputAttachment = (id, patch) => {
    if (!id) return;
    setInputAttachments((prev) =>
      prev.map((a) => (a?.id === id ? { ...a, ...patch } : a))
    );
  };

  const removeInputAttachment = (id) => {
    setInputAttachments((prev) => {
      const target = prev.find((a) => a?.id === id);
      if (target?.previewUrl) {
        try {
          URL.revokeObjectURL(target.previewUrl);
          objectUrlsRef.current.delete(target.previewUrl);
        } catch (e) {
          // noop
        }
      }
      return prev.filter((a) => a?.id !== id);
    });
  };

  const uploadFileToGenerator = async (file, opts = {}) => {
    if (!file) return;

    // 파일 크기 제한 (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("파일 크기는 10MB 이하여야 합니다.");
      return;
    }

    // 지원하는 파일 타입 확인
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
    ];
    if (!allowedTypes.includes(file.type)) {
      alert("지원하는 파일 형식: 이미지 (JPEG, PNG, GIF, WebP) 또는 PDF");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await apiForm("/generator/upload-file", formData);

      const data = await response
        .json()
        .catch(() => ({ error: `HTTP ${response.status}` }));

      if (!response.ok) {
        // Flask-JWT-Extended는 {msg: "..."} 형태로 오기도 해서 둘 다 커버
        const message = data?.error || data?.message || data?.msg || "파일 업로드 실패";
        throw new Error(message);
      }

      if (opts?.attachmentId) {
        updateInputAttachment(opts.attachmentId, {
          status: "done",
          fileUrl: data.file_url || "",
          errorMessage: "",
          processed_content: data.processed_content || "",
        });
      }

      // 업로드된 파일 정보 저장
      setUploadedFiles((prev) => [
        ...prev,
        {
          id: data.file_id || Date.now().toString(),
          name: file.name,
          type: file.type,
          size: file.size,
          processed_content: data.processed_content,
          file_url: data.file_url,
        },
      ]);

      // NOTE: processed_content는 유저에게 노출하지 않고, 프론트 상태에만 저장합니다.
    } catch (error) {
      console.error("파일 업로드 오류:", error);
      if (opts?.attachmentId) {
        updateInputAttachment(opts.attachmentId, {
          status: "error",
          errorMessage: error?.message || String(error),
        });
      }
      alert(`파일 업로드 실패: ${error.message}`);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // 파일 업로드 (파일 선택)
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // 최대 5개 제한
    if (inputAttachments.length >= 5) {
      alert("첨부파일은 최대 5개까지 추가할 수 있습니다.");
      event.target.value = ""; // 파일 입력 초기화
      return;
    }
    
    // 파일 크기 제한 검증 (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("파일 크기는 10MB 이하여야 합니다.");
      event.target.value = ""; // 파일 입력 초기화
      return;
    }
    
    // 지원하는 파일 타입 확인
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
    ];
    if (!allowedTypes.includes(file.type)) {
      alert("지원하는 파일 형식: 이미지 (JPEG, PNG, GIF, WebP) 또는 PDF");
      event.target.value = ""; // 파일 입력 초기화
      return;
    }
    
    const isImage = (file.type || "").startsWith("image/");
    const previewUrl = isImage ? URL.createObjectURL(file) : null;
    if (previewUrl) objectUrlsRef.current.add(previewUrl);
    const attachmentId = addInputAttachment(file, previewUrl);
    await uploadFileToGenerator(file, { attachmentId });
    event.target.value = ""; // 파일 입력 초기화 (다음 파일 선택을 위해)
  };

  // 이미지 Ctrl+V 붙여넣기 업로드
  const handleChatInputPaste = async (event) => {
    const items = event.clipboardData?.items;
    if (!items || items.length === 0) return;

    // 이미지가 있으면 우선 처리 (텍스트 붙여넣기와 충돌 방지)
    const imageItem = Array.from(items).find(
      (it) => it.kind === "file" && (it.type || "").startsWith("image/")
    );
    if (!imageItem) return;

    const file = imageItem.getAsFile();
    if (!file) return;

    // 최대 5개 제한
    if (inputAttachments.length >= 5) {
      alert("첨부파일은 최대 5개까지 추가할 수 있습니다.");
      return;
    }

    // 파일 크기 제한 검증 (10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      alert("파일 크기는 10MB 이하여야 합니다.");
      return;
    }
    
    // 지원하는 파일 타입 확인
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/webp",
      "application/pdf",
    ];
    if (!allowedTypes.includes(file.type)) {
      alert("지원하는 파일 형식: 이미지 (JPEG, PNG, GIF, WebP) 또는 PDF");
      return;
    }

    event.preventDefault();
    const previewUrl = URL.createObjectURL(file);
    objectUrlsRef.current.add(previewUrl);
    const attachmentId = addInputAttachment(file, previewUrl);
    await uploadFileToGenerator(file, { attachmentId });
  };

  const autoResizeChatTextarea = () => {
    const el = chatTextareaRef.current;
    if (!el) return;
    
    const wrap = el.closest('.csm-textarea-wrap');
    const hasAttachments = inputAttachments.length > 0;
    const maxHeight = 250; // 최대 높이 250px
    
    if (hasAttachments) {
      // 파일이 첨부된 경우: wrap을 250px로 고정
      if (wrap) {
        wrap.style.height = `${maxHeight}px`;
        wrap.style.maxHeight = `${maxHeight}px`;
      }
      // textarea는 flex: 1로 남은 공간을 채우므로 높이 조정 불필요
      el.style.height = "auto";
      el.style.overflowY = "auto";
    } else {
      // 파일이 없는 경우: textarea 높이를 내용에 맞춰 조정
      el.style.height = "0px";
      const next = Math.min(el.scrollHeight, maxHeight);
      const textareaHeight = Math.max(next, 42);
      el.style.height = `${textareaHeight}px`;
      el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden";
      
      // wrap의 높이도 textarea 높이에 맞춰 조정
      if (wrap) {
        wrap.style.height = `${textareaHeight}px`;
        wrap.style.maxHeight = `${maxHeight}px`;
      }
    }
  };

  useEffect(() => {
    autoResizeChatTextarea();
  }, [chatInput, inputAttachments]);

  useEffect(() => {
    return () => {
      try {
        objectUrlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      } catch (e) {
        // noop
      }
      objectUrlsRef.current.clear();
    };
  }, [isB2BMode]);

  // 스크롤 위치 감지 및 플로팅 버튼 표시
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;

    const checkScrollPosition = () => {
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      const isNearBottom = distanceFromBottom < 100;
      setShowScrollToBottom(!isNearBottom);
    };

    el.addEventListener('scroll', checkScrollPosition);
    checkScrollPosition(); // 초기 체크

    return () => {
      el.removeEventListener('scroll', checkScrollPosition);
    };
  }, [messages.length]);

  // 경계선이 고정된 상태에서 새 메시지가 추가될 때 스크롤 위치 유지
  useEffect(() => {
    const pinnedIndex = pinnedDividerIndexRef.current;
    if (pinnedIndex === null) return;
    
    const chatArea = chatScrollRef.current;
    if (!chatArea) return;
    
    const dividerRef = dividerRefs.current[`divider-${pinnedIndex}`];
    if (!dividerRef) return;
    
    const chatContainer = chatArea.querySelector('.csm-chat');
    if (!chatContainer) return;
    
    // 경계선이 상단에 유지되도록 스크롤 위치 조정
    requestAnimationFrame(() => {
      const offsetTop = dividerRef.offsetTop - chatContainer.offsetTop;
      if (Math.abs(chatArea.scrollTop - offsetTop) > 5) {
        chatArea.scrollTop = offsetTop;
      }
    });
  }, [messages]);

  // 채팅창 내부에서만(페이지 스크롤 X) 새 메시지가 도착하면 최근 메시지가 보이도록 스크롤
  useEffect(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = loading;

    // 로딩이 시작되거나 완료되면 최근 사용자 메시지 또는 단계 전환 경계선이 채팅창 가장 상단에 오도록 스크롤
    if (messages.length > 0 && pinnedDividerIndexRef.current === null) {
      const scrollToRelevantMessage = () => {
        requestAnimationFrame(() => {
          const chatArea = chatScrollRef.current;
          if (!chatArea) return;
          
          // 최근 메시지부터 역순으로 검색
          for (let i = messages.length - 1; i >= 0; i--) {
            const msg = messages[i];
            const chatContainer = chatArea.querySelector('.csm-chat');
            if (!chatContainer) return;
            
            // 경계선만 있는 메시지가 있으면 우선 스크롤
            if (msg.type === "divider" && msg.isDividerOnly) {
              const dividerRef = dividerRefs.current[`divider-${i}`];
              if (dividerRef) {
                const offsetTop = dividerRef.offsetTop - chatContainer.offsetTop;
                chatArea.scrollTo({ top: offsetTop, behavior: "smooth" });
                setShowScrollToBottom(false);
                return;
              }
            }
            // 단계 전환 메시지가 있으면 경계선을 스크롤
            if (msg.isStepTransition) {
              const dividerRef = dividerRefs.current[`divider-${i}`];
              if (dividerRef) {
                const offsetTop = dividerRef.offsetTop - chatContainer.offsetTop;
                chatArea.scrollTo({ top: offsetTop, behavior: "smooth" });
                setShowScrollToBottom(false);
                return;
              }
              // 경계선 ref가 없으면 메시지 ref로 대체
              const msgRef = messageRefs.current[`msg-${i}`];
              if (msgRef) {
                const offsetTop = msgRef.offsetTop - chatContainer.offsetTop;
                chatArea.scrollTo({ top: offsetTop, behavior: "smooth" });
                setShowScrollToBottom(false);
                return;
              }
            }
            // 사용자 메시지가 있으면 그것으로 스크롤
            if (msg.type === 'user') {
              const userMsgRef = messageRefs.current[`msg-${i}`];
              if (userMsgRef) {
                const offsetTop = userMsgRef.offsetTop - chatContainer.offsetTop;
                chatArea.scrollTo({ top: offsetTop, behavior: "smooth" });
                setShowScrollToBottom(false);
                return;
              }
              break; // 가장 최근 사용자 메시지 하나만 스크롤
            }
          }
        });
      };

      // 로딩이 시작될 때 (false -> true)
      if (!wasLoading && loading) {
        scrollToRelevantMessage();
        return;
      }

      // 로딩이 완료될 때 (true -> false)
      if (wasLoading && !loading) {
        scrollToRelevantMessage();
        return;
      }
    }
  }, [loading, messages]);

  // 플로팅 버튼 클릭 시 아래로 스크롤
  const handleScrollToBottom = () => {
    const scrollToBottom = () => {
      if (chatEndRef.current && typeof chatEndRef.current.scrollIntoView === "function") {
        chatEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
        return;
      }
      const el = chatScrollRef.current;
      if (!el) return;
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    };
    requestAnimationFrame(() => {
      scrollToBottom();
      setTimeout(() => setShowScrollToBottom(false), 300);
    });
  };

  const handleSendMessage = async () => {
    if (loading) return;

    const userText = (chatInput || "").trim();
    const imageAttachments = inputAttachments.filter(
      (a) => (a?.type || "").startsWith("image/") && a?.previewUrl && a?.status === "done"
    );
    if (!userText && imageAttachments.length === 0) return; // 빈 메시지는 전송하지 않음

    const attachmentsContext = inputAttachments
      .filter((a) => a?.status === "done" && a?.processed_content)
      .map((a) => {
        const name = a?.name || "file";
        const content = String(a.processed_content || "").trim();
        return content ? `[파일 첨부: ${name}]\n${content}` : "";
      })
      .filter(Boolean)
      .join("\n\n");

    // 말풍선 닫기
    setShowNextStepHint(false);

    setLoading(true);
    setChatInput("");
    if (chatInputRef.current) {
      chatInputRef.current.value = "";
    }

    // 사용자 메시지 추가 (attachmentsContext는 UI에는 숨기고, API에만 포함)
    // 첨부파일의 previewUrl은 채팅 메시지에 저장되므로 revoke하지 않음
    const savedAttachments = imageAttachments.map((a) => ({
      kind: "image",
      previewUrl: a.previewUrl,
      name: a.name,
    }));
    
    addMessage({
      type: "user",
      content: userText,
      hidden_context: attachmentsContext,
      attachments: savedAttachments,
    });
    
    // inputAttachments만 초기화 (previewUrl은 채팅 메시지에서 사용 중이므로 revoke하지 않음)
    setInputAttachments([]);
    // 전송하면 방금 보낸 메시지가 채팅창 가장 상단에 오도록 스크롤
    requestAnimationFrame(() => {
      const userMsgIndex = messages.length; // addMessage 직후이므로 현재 messages.length가 새 메시지 인덱스
      setTimeout(() => {
        const chatArea = chatScrollRef.current;
        const userMsgRef = messageRefs.current[`msg-${userMsgIndex}`];
        if (chatArea && userMsgRef) {
          const offsetTop = userMsgRef.offsetTop;
          chatArea.scrollTo({ top: offsetTop, behavior: "smooth" });
        }
      }, 100);
    });

    try {
      const nextConversation = messages.concat([
        { type: "user", content: userText, hidden_context: attachmentsContext },
      ]);

      const conversationForApi = nextConversation
        .slice(-24)
        .map((m) => ({
          type: m.type,
          content: m?.hidden_context ? `${m.content}\n\n${m.hidden_context}` : m.content,
        }));

      const payload = {
        step: stepIndex,
        mode: "recommend",
        conversation: conversationForApi,
        ledger_cards: ledgerCards,
        projectId,
        project_info: project
          ? {
              id: project.id,
              name: project.name,
              tags: project.tags || [],
              description: project.description || "",
            }
          : null,
      };

      const res = await sendConversationMessage(payload);

      // 백엔드가 생성한 메시지가 있으면 표시
      if (res?.message) {
        addMessage({ type: "ai", content: res.message });
      }

      // 추천 카드들 표시
      const rawCards = Array.isArray(res?.draft_cards) ? res.draft_cards : [];
      const normalizedDraft = rawCards.map((c, idx) => normalizeCard(c, idx));

      // 질문형 카드(…question)는 후보 카드 영역에 나오면 안 됨 → 채팅 질문으로 1개만 노출
      const questionLike = normalizedDraft.filter(isQuestionLikeCard);
      const selectionLike = normalizedDraft.filter((c) => !isQuestionLikeCard(c));
      // 왼쪽 하단 카드 리스트 대신, 채팅 타임라인에 “후보 항목”으로 노출
      if (selectionLike.length > 0) {
        addMessage({
          type: "ai",
          content: "후보 항목이 생성됐어요. 필요한 것만 선택해 주세요.",
          recommendations: selectionLike,
        });
      }

      // 백엔드가 next_question으로 생성한 질문 표시
      if (res?.next_question && (res.next_question.content || res.next_question.title)) {
        const qText = String(res.next_question.content || res.next_question.title || "").trim();
        if (qText) {
          addMessage({ 
            type: "ai", 
            content: qText
          });
        }
      } else if (questionLike.length > 0) {
        const q = questionLike[0];
        addMessage({
          type: "ai",
          content: q?.content || q?.title || "추가로 알려줄 수 있을까요?",
        });
      }
    } catch (e) {
      addMessage({
        type: "ai",
        content: `오류가 발생했습니다: ${e.message || String(e)}`,
      });
    } finally {
      setLoading(false);
      // chatInput과 inputAttachments는 함수 시작 부분에서 이미 초기화됨
    }
  };


  const handleNextStep = async () => {
    if (loading) return; // 로딩 중이면 중복 실행 방지

    let nextStepIndex = Math.min(stepIndex + 1, STEPS.length - 1);
    
    // 다음 단계로 이동
    setStepIndex(nextStepIndex);
    setDraftCards([]);
    
    // 경계선을 먼저 즉시 추가 (별도 메시지로)
    const dividerMsgIndex = messages.length;
    pinnedDividerIndexRef.current = dividerMsgIndex; // 경계선 인덱스 저장
    addMessage({
      type: "divider",
      step: nextStepIndex + 1,
      isDividerOnly: true
    });
    
    // 경계선이 상단에 오도록 스크롤
    requestAnimationFrame(() => {
      setTimeout(() => {
        const chatArea = chatScrollRef.current;
        const dividerRef = dividerRefs.current[`divider-${dividerMsgIndex}`];
        if (chatArea && dividerRef) {
          const offsetTop = dividerRef.offsetTop;
          chatArea.scrollTo({ top: offsetTop, behavior: "smooth" });
        }
      }, 100);
    });
    
    // 이전 단계에서 선택된 내용을 기반으로 맞춤형 시작 메시지 생성
    setLoading(true);
    
    try {
      // 빈 대화로 시작하되, ledger_cards는 전달하여 이전 선택 반영
      const payload = {
        step: nextStepIndex,
        mode: "step_transition", // 단계 전환 모드
        conversation: [], // 새 단계이므로 대화는 비움
        ledger_cards: ledgerCards, // 이전 단계에서 선택된 카드들
        projectId,
        project_info: project
          ? {
              id: project.id,
              name: project.name,
              tags: project.tags || [],
              description: project.description || "",
            }
          : null,
      };

      const res = await sendConversationMessage(payload);
      
      // 기본 프롬프트는 항상 먼저 표시 (모든 단계에서 일관성 유지)
      addMessage({ 
        type: "ai", 
        content: STEP_START_PROMPTS[nextStepIndex]
      });
      
      // 백엔드가 생성한 맞춤형 메시지가 있으면 추가로 표시 (이전 선택을 언급하는 등)
      const hasPreviousSelections = ledgerCards.length > 0;
      if (hasPreviousSelections && res?.message && res.message.trim()) {
        addMessage({ 
          type: "ai", 
          content: res.message
        });
      }
      
      // 추천 카드들 표시
      const rawCards = Array.isArray(res?.draft_cards) ? res.draft_cards : [];
      const normalizedDraft = rawCards.map((c, idx) => normalizeCard(c, idx));

      // 질문형 카드(…question)는 후보 카드 영역에 나오면 안 됨 → 채팅 질문으로 1개만 노출
      const questionLike = normalizedDraft.filter(isQuestionLikeCard);
      const selectionLike = normalizedDraft.filter((c) => !isQuestionLikeCard(c));
      
      // 채팅 타임라인에 "후보 항목"으로 노출
      if (selectionLike.length > 0) {
        addMessage({
          type: "ai",
          content: "후보 항목이 생성됐어요. 필요한 것만 선택해 주세요.",
          recommendations: selectionLike,
        });
      }
      
      // 백엔드가 next_question으로 생성한 질문 표시
      if (res?.next_question && (res.next_question.content || res.next_question.title)) {
        const qText = String(res.next_question.content || res.next_question.title || "").trim();
        if (qText) {
          addMessage({ type: "ai", content: qText });
        }
      } else if (questionLike.length > 0) {
        const q = questionLike[0];
        addMessage({
          type: "ai",
          content: q?.content || q?.title || "추가로 알려줄 수 있을까요?",
        });
      }
      
      // draftCards 상태 업데이트 (필요한 경우)
      setDraftCards(selectionLike);
      
    } catch (e) {
      // 에러 시 기본 프롬프트 사용
      addMessage({ type: "ai", content: STEP_START_PROMPTS[nextStepIndex] });
    } finally {
      setLoading(false);
      setHasAskedFirstQuestionByStep((flags) => {
        const updated = [...flags];
        updated[nextStepIndex] = false;
        return updated;
      });
    }
  };

  const handleFinalize = async () => {
    if (finalizeLoading) return;
    
    // ledgerCards가 없으면 생성 불가
    if (ledgerCards.length === 0) {
      addMessage({
        type: "ai",
        content: "오른쪽에 선택된 카드가 없습니다. 최소한 몇 개는 선택한 뒤 최종 생성을 해주세요.",
      });
      return;
    }

    setFinalizeLoading(true);
    try {
      let currentProject = projectSlug ? getProject(projectSlug) : null;
      if (!workspaceLoaded) {
        const loadedProjects = await loadProjects();
        if (!Array.isArray(loadedProjects)) {
          throw new Error("프로젝트 정보를 불러오지 못했습니다. 다시 시도해주세요.");
        }
        currentProject = projectSlug ? getProject(projectSlug) : null;
      }
      let finalName = (studyNameRef.current || "").trim();
      let targetProjectId = currentProject?.id ?? null;

      // 리서치명이 비어있으면 NewStudyPage처럼 자동 생성
      if (!finalName) {
        try {
          const seed = buildStudyNameSeed({ ledgerCards, messages });
          const nameRes = await generateStudyName(seed);
          if (nameRes?.success && nameRes?.studyName) {
            finalName = nameRes.studyName;
            studyNameRef.current = nameRes.studyName;
          } else {
            finalName = `리서치-${Date.now()}`;
            studyNameRef.current = finalName;
          }
        } catch (e) {
          finalName = `리서치-${Date.now()}`;
          studyNameRef.current = finalName;
        }
      }

      // projectId가 없을 때: 프로젝트 먼저 생성
      if (!targetProjectId) {
        // ⭐ Tier 체크: 프로젝트 생성 가능 여부 확인
        if (!canCreateProject()) {
          addMessage({
            type: "ai",
            content: "현재 플랜에서는 프로젝트 생성이 제한됩니다. (Tier 제한)",
          });
          setFinalizeLoading(false);
          return;
        }

        // 프로젝트명 생성
        const projectNameResult = await generateProjectName(
          finalName || "새 리서치",
          ledgerCards.map(c => `${c.title || ''}: ${c.content || ''}`).join('\n').substring(0, 500)
        );

        if (!projectNameResult.success) {
          addMessage({
            type: "ai",
            content: "프로젝트명 생성에 실패했습니다.",
          });
          setFinalizeLoading(false);
          return;
        }

        const autoProjectName = projectNameResult.projectName;
        const autoTags = projectNameResult.tags || [];

        // 프로젝트 생성
        const projectData = {
          name: autoProjectName,
          domain: "general",
          tags: autoTags,
        };

        const createdProject = await createProjectInContext(projectData);

        if (createdProject && createdProject.id) {
          targetProjectId = createdProject.id;
        } else {
          addMessage({
            type: "ai",
            content: "프로젝트 생성에 실패했습니다.",
          });
          setFinalizeLoading(false);
          return;
        }
      }

      // 스터디 생성 가능 여부 체크
      if (!canCreateStudy(targetProjectId)) {
        addMessage({
          type: "ai",
          content: "현재 플랜에서는 스터디 생성이 제한됩니다. (Tier 제한)",
        });
        setFinalizeLoading(false);
        return;
      }

      if (!activeRequestIdRef.current) activeRequestIdRef.current = makeRequestId();
      const requestId = activeRequestIdRef.current;

      const res = await conversationMakerFinalizeOneshot({
        projectId: targetProjectId,
        studyName: finalName,
        ledger_cards: ledgerCards,
        requestId,
      });

      if (res?.success && (res.study_slug || res.study_id)) {
        activeRequestIdRef.current = null;
        const slug = res.study_slug || res.study_id;
        navigate(`/study/${slug}`);
        return;
      }

      throw new Error(res?.error || "최종 생성에 실패했습니다.");
    } catch (e) {
      addMessage({
        type: "ai",
        content: `최종 생성 오류: ${e.message || String(e)}`,
      });
    } finally {
      setFinalizeLoading(false);
    }
  };

  // 다음 단계 힌트는 CSS로 처리 (wrapper 내부에 absolute positioning)

  // 페이지 초기 렌더링 완료 감지 - 헤더 레이아웃 시프트 방지
  useEffect(() => {
    // requestAnimationFrame을 두 번 사용하여 브라우저가 레이아웃을 계산한 후 콘텐츠 표시
    // 첫 번째 프레임: DOM 업데이트
    requestAnimationFrame(() => {
      // 두 번째 프레임: 레이아웃 계산 완료 후
      requestAnimationFrame(() => {
        setIsPageReady(true);
      });
    });
  }, []);

  // 이 페이지에서는 '전체(workspace-body) 스크롤'을 잠그고, 좌/우 섹션 내부에서만 스크롤되게 함
  // (:has() 기반 CSS는 환경에 따라 불안정할 수 있어, 클래스 토글로 확실히 처리)
  useEffect(() => {
    const bodyEl = document?.querySelector?.(".workspace-body, .b2b-body");
    const layoutEl = document?.querySelector?.(".workspace-layout, .b2b-layout");
    const contentEl = document?.querySelector?.(".workspace-content, .b2b-content");
    if (!bodyEl) return undefined;

    // workspace-body는 기본적으로 overflow-y:auto라서 페이지 전체 스크롤처럼 보일 수 있음
    // 또한 layout/content가 100vh로 고정되지 않으면 문서(body)가 커지면서 document 스크롤이 생길 수 있음
    layoutEl?.classList?.add("workspace-layout--scroll-locked");
    layoutEl?.classList?.add("b2b-layout--scroll-locked");
    contentEl?.classList?.add("workspace-content--scroll-locked");
    contentEl?.classList?.add("b2b-content--scroll-locked");
    bodyEl.classList.add("workspace-body--scroll-locked");
    bodyEl.classList.add("b2b-body--scroll-locked");
    return () => {
      bodyEl.classList.remove("workspace-body--scroll-locked");
      bodyEl.classList.remove("b2b-body--scroll-locked");
      layoutEl?.classList?.remove("workspace-layout--scroll-locked");
      layoutEl?.classList?.remove("b2b-layout--scroll-locked");
      contentEl?.classList?.remove("workspace-content--scroll-locked");
      contentEl?.classList?.remove("b2b-content--scroll-locked");
    };
  }, []);

  // 페이지 초기 로딩 중일 때 스피너 표시
  if (!isPageReady) {
    return (
      <Layout defaultCollapsed={true}>
        <div className="conversation-study-maker" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '2rem', color: '#666' }} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout defaultCollapsed={true}>
      <div className="conversation-study-maker">
        <div className="csm-grid csm-grid-fill">
          <div className="csm-panel csm-panel-left csm-chat-section">
            <div className="csm-panel-header csm-panel-header-left">
              <div className="csm-progress-bar">
                {STEPS.map((s, idx) => (
                  <React.Fragment key={s.id}>
                    <div className={`csm-step-box ${
                      idx === stepIndex
                        ? 'csm-step-active'
                        : idx < stepIndex
                          ? 'csm-step-completed'
                          : 'csm-step-pending'
                    }`}>
                      <span className="csm-step-number">{idx + 1}</span>
                      {idx === stepIndex && (
                        <span className="csm-step-title">{s.title}</span>
                      )}
                    </div>
                    {idx < STEPS.length - 1 && (
                      <div className={`csm-step-connector ${
                        idx < stepIndex ? 'csm-step-connector-completed' : ''
                      }`}></div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div
              className="csm-body csm-body-left csm-section-body csm-chat-section-body"
            >
              <div ref={chatScrollRef} className="csm-chat-area">
                <div className="csm-chat">
                  {messages.map((m, idx) => {
                    // 경계선만 있는 메시지인지 확인
                    if (m.type === "divider" && m.isDividerOnly) {
                      return (
                        <div 
                          key={idx}
                          className="csm-step-divider"
                          ref={(el) => {
                            if (el) {
                              dividerRefs.current[`divider-${idx}`] = el;
                            }
                          }}
                        >
                          <div className="csm-step-divider-line"></div>
                          <div className="csm-step-divider-label">
                            {STEPS[m.step - 1]?.title || `${m.step}단계`} 시작
                          </div>
                          <div className="csm-step-divider-line"></div>
                        </div>
                      );
                    }
                    
                    const isStepTransition = m.isStepTransition;
                    const prevMessage = idx > 0 ? messages[idx - 1] : null;
                    const showDivider = isStepTransition && (!prevMessage || !prevMessage.isStepTransition);
                    
                    return (
                      <React.Fragment key={idx}>
                        {showDivider && (
                          <div 
                            className="csm-step-divider"
                            ref={(el) => {
                              if (el) {
                                dividerRefs.current[`divider-${idx}`] = el;
                              }
                            }}
                          >
                            <div className="csm-step-divider-line"></div>
                            <div className="csm-step-divider-label">
                              {STEPS[m.step - 1]?.title || `${m.step}단계`} 시작
                            </div>
                            <div className="csm-step-divider-line"></div>
                          </div>
                        )}
                        <div 
                          ref={(el) => {
                            if (el) {
                              messageRefs.current[`msg-${idx}`] = el;
                            }
                          }}
                          className={`csm-message ${m.type} ${isStepTransition ? 'csm-message-step-transition' : ''}`}
                        >
                          <div className="csm-message-content">
                            {m.type === 'ai' ? (
                              <>
                                <div className="csm-message-avatar">
                                  <FontAwesomeIcon icon={faRobot} />
                                </div>
                                <div className="csm-message-bubble">
                                  {m.content}
                                  {Array.isArray(m.recommendations) && m.recommendations.length > 0 && (() => {
                                    const INITIAL_SHOW_COUNT = 5;
                                    const shouldShowMore = m.recommendations.length > INITIAL_SHOW_COUNT;
                                    const isExpanded = expandedRecommendations.has(idx);
                                    const visibleRecommendations = shouldShowMore && !isExpanded
                                      ? m.recommendations.slice(0, INITIAL_SHOW_COUNT)
                                      : m.recommendations;
                                    const remainingCount = m.recommendations.length - INITIAL_SHOW_COUNT;
                                    
                                    return (
                                      <div className="csm-recommendations">
                                        {visibleRecommendations.map((r) => {
                                          const alreadySelected = ledgerCards.some((c) => c.id === r.id);
                                          const isInsert = r?._action === "insert_to_input";
                                          return (
                                            <div
                                              key={r.id}
                                              className="csm-recommendation"
                                            >
                                              <div className="csm-recommendation-body">
                                                <div className="csm-recommendation-title">
                                                  {r.title || "후보 항목"}
                                                  <span className="csm-recommendation-tag">{r.type}</span>
                                                </div>
                                                <div className="csm-recommendation-content">{r.content}</div>
                                              </div>
                                              <button
                                                type="button"
                                                className="csm-recommendation-cta-btn"
                                                disabled={loading || (alreadySelected && !isInsert)}
                                                onClick={() => {
                                                  if (isInsert) {
                                                    setChatInput((prev) => {
                                                      const text = String(r?.content || "").trim();
                                                      if (!text) return prev;
                                                      return prev ? `${prev}\n\n${text}` : text;
                                                    });
                                                    return;
                                                  }
                                                  addToLedger(r);
                                                }}
                                                title={isInsert ? "입력창에 추가" : (alreadySelected ? "이미 선택됨" : "선택하기")}
                                              >
                                                {isInsert ? "입력에 추가" : (alreadySelected ? "선택됨" : "선택하기")}
                                              </button>
                                            </div>
                                          );
                                        })}
                                        {shouldShowMore && (
                                          <button
                                            type="button"
                                            className="csm-recommendations-more"
                                            onClick={() => {
                                              const newExpanded = new Set(expandedRecommendations);
                                              if (isExpanded) {
                                                newExpanded.delete(idx);
                                              } else {
                                                newExpanded.add(idx);
                                              }
                                              setExpandedRecommendations(newExpanded);
                                            }}
                                          >
                                            {isExpanded ? (
                                              <>
                                                <span>접기</span>
                                                <span className="csm-recommendations-more-icon">▲</span>
                                              </>
                                            ) : (
                                              <>
                                                <span>더 보기 ({remainingCount}개)</span>
                                                <span className="csm-recommendations-more-icon">▼</span>
                                              </>
                                            )}
                                          </button>
                                        )}
                                      </div>
                                    );
                                  })()}
                                </div>
                              </>
                            ) : (
                              <div className="csm-message-bubble">
                                <ReactMarkdown>{m.content}</ReactMarkdown>
                              {Array.isArray(m.attachments) && m.attachments.length > 0 && (() => {
                                const imageAttachments = m.attachments.filter((a) => a?.kind === "image" && a?.previewUrl);
                                const isExpanded = expandedMessageAttachments.has(idx);
                                const visibleImages = isExpanded ? imageAttachments : imageAttachments.slice(0, 2);
                                const hasMore = imageAttachments.length > 2;
                                
                                return (
                                  <div className="csm-message-attachments">
                                    {visibleImages.map((a, aIdx) => (
                                      <img
                                        key={aIdx}
                                        className="csm-message-attachment-img"
                                        src={a.previewUrl}
                                        alt={a.name || "첨부 이미지"}
                                      />
                                    ))}
                                    {hasMore && (
                                      <button
                                        type="button"
                                        className="csm-message-attachments-more"
                                        onClick={() => {
                                          const newExpanded = new Set(expandedMessageAttachments);
                                          if (isExpanded) {
                                            newExpanded.delete(idx);
                                          } else {
                                            newExpanded.add(idx);
                                          }
                                          setExpandedMessageAttachments(newExpanded);
                                        }}
                                      >
                                        {isExpanded ? (
                                          <>접기 <span className="csm-recommendations-more-icon">▲</span></>
                                        ) : (
                                          <>+{imageAttachments.length - 2}개 더보기 <span className="csm-recommendations-more-icon">▼</span></>
                                        )}
                                      </button>
                                    )}
                                  </div>
                                );
                              })()}
                              </div>
                            )}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  {loading && (
                    <div className="csm-message ai">
                      <div className="csm-message-content">
                        <div className="csm-message-avatar">
                          <FontAwesomeIcon icon={faRobot} />
                        </div>
                        <div className="csm-message-bubble csm-message-loading">
                          <FontAwesomeIcon icon={faSpinner} spin />
                          <span>답변 생성 중...</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div ref={chatEndRef} />
                </div>
              </div>

              <div className="csm-input-dock">
                <div className="csm-input-wrap">
                  <div className="csm-input">
                    <button
                      className="csm-attach-btn"
                      type="button"
                      disabled={loading || inputAttachments.length >= 5}
                      onClick={() => fileInputRef.current?.click()}
                      title={inputAttachments.length >= 5 ? "첨부파일은 최대 5개까지 추가할 수 있습니다" : "파일 첨부"}
                      aria-label="파일 첨부"
                    >
                      <FontAwesomeIcon icon={faPaperclip} />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*,.pdf"
                      onChange={handleFileUpload}
                      style={{ display: 'none' }}
                    />
                    <div className="csm-textarea-wrap">
                      {inputAttachments.length > 0 && (
                        <div className="csm-textarea-previews" aria-label="첨부 파일 미리보기">
                          {inputAttachments.map((a) => {
                            const isImage = (a?.type || "").startsWith("image/") && a?.previewUrl;
                            const isFile = !isImage;
                            const fileName = a?.name || "file";
                            const fileExtension = fileName.includes('.') ? fileName.split('.').pop().toLowerCase() : '';
                            const fileNameWithoutExt = fileName.includes('.') ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName;
                            const displayFileName = fileNameWithoutExt.length > 8 
                              ? fileNameWithoutExt.substring(0, 8) + '...' 
                              : fileNameWithoutExt;
                            const displayFileText = fileExtension 
                              ? `${displayFileName}.${fileExtension}` 
                              : displayFileName;
                            
                            return (
                              <div key={a.id} className="csm-textarea-preview">
                                {isImage ? (
                                  <img
                                    className="csm-textarea-preview-img"
                                    src={a.previewUrl}
                                    alt={a.name || "첨부 이미지"}
                                  />
                                ) : (
                                  <div className="csm-textarea-preview-file">
                                    <div className="csm-textarea-preview-file-icon">
                                      <FontAwesomeIcon icon={faFile} />
                                    </div>
                                    <div className="csm-textarea-preview-file-name">
                                      {displayFileText}
                                    </div>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  className="csm-textarea-preview-remove"
                                  onClick={() => removeInputAttachment(a.id)}
                                  title="첨부 제거"
                                  aria-label="첨부 제거"
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      <textarea
                        ref={(el) => {
                          chatTextareaRef.current = el;
                          chatInputRef.current = el;
                        }}
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onInput={autoResizeChatTextarea}
                        onPaste={handleChatInputPaste}
                        placeholder="메시지를 입력하세요"
                        rows={1}
                        onFocus={() => setShowNextStepHint(false)}
                        onCompositionStart={() => setIsComposing(true)}
                        onCompositionEnd={() => setIsComposing(false)}
                        onKeyDown={(e) => {
                          // Enter: 전송 / Shift+Enter: 줄바꿈
                          if (
                            e.key === "Enter" &&
                            !e.shiftKey &&
                            !loading &&
                            !isComposing &&
                            !e.isComposing
                          ) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                      />
                    </div>
                    <button
                      className={`csm-btn ${(chatInput.trim() || inputAttachments.some((a) => a?.status === "done")) ? 'primary' : ''}`}
                      type="button"
                      disabled={loading || (!chatInput.trim() && !inputAttachments.some((a) => a?.status === "done"))}
                      onClick={handleSendMessage}
                    >
                      {loading ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPaperPlane} />}
                    </button>
                    <div className="csm-next-step-button-wrapper">
                      <button 
                        ref={nextStepButtonRef}
                        className={`csm-btn ${canProceedToNextStep() && !loading ? 'primary' : ''}`}
                        type="button" 
                        onClick={handleNextStep} 
                        disabled={loading || !canProceedToNextStep()}
                        title={
                          loading
                            ? "처리 중입니다..."
                            : !canProceedToNextStep()
                              ? stepIndex === 0
                                ? "1단계에서 최소 1개 카드를 선택해주세요."
                                : `${STEPS[stepIndex].title}에서 최소 1개 카드를 선택해주세요.`
                              : ""
                        }
                      >
                        다음 단계
                      </button>
                      {/* 다음 단계 버튼 위 말풍선 (버튼과 함께 배치) */}
                      {showNextStepHint && !canProceedToNextStep() && !loading && (
                        <div className="csm-next-step-hint">
                          <button
                            className="csm-next-step-hint-close"
                            type="button"
                            onClick={() => setShowNextStepHint(false)}
                            aria-label="닫기"
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                          <div className="csm-next-step-hint-content">
                            하나 이상의 항목을 선택하면 다음 단계로 넘어갈 수 있어요
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              {/* 왼쪽 섹션은 채팅만 사용 (후보/선택은 채팅 타임라인에 남김) */}
            </div>
          </div>

          <div className="csm-panel csm-panel-right csm-preview-section">
            <div className="csm-panel-header">
              <h2>계획서 프리뷰</h2>
              <div className="csm-header-actions">
                <div className="csm-step">선택 {ledgerCards.length}개</div>
              </div>
            </div>
            <div className="csm-body csm-body-fill csm-section-body csm-preview-section-body">
              <div className="csm-preview-scroll">
              {(() => {
                // ledgerCards를 카테고리별로 그룹화
                const groupedCards = buildPlanDraft(ledgerCards);
                
                if (ledgerCards.length === 0) {
                  return (
                    <div className="csm-empty">
                      선택한 항목이 여기에 쌓입니다.
                      <br />
                    </div>
                  );
                }

                // 카테고리 순서 정의 (나중에 계획서 구조가 될 순서)
                const categoryOrder = ['context', 'rq', 'method', 'audience', 'extra'];
                const INITIAL_SHOW_COUNT = 3; // 기본 표시 개수
                
                return (
                  <div className="csm-plan-preview">
                    {categoryOrder.map((key) => {
                      const items = groupedCards.sections[key] || [];
                      // 카테고리 내에 카드가 하나라도 있으면 표시
                      if (items.length === 0) return null;
                      
                      const shouldShowMore = items.length > INITIAL_SHOW_COUNT;
                      const isExpanded = expandedSections.has(key);
                      const visibleItems = shouldShowMore && !isExpanded
                        ? items.slice(0, INITIAL_SHOW_COUNT)
                        : items;
                      const remainingCount = items.length - INITIAL_SHOW_COUNT;
                      
                      return (
                        <div key={key} className="csm-plan-section">
                          <div className="csm-plan-section-title">
                            {SECTION_LABELS[key] || key}
                            {items.length > 0 && (
                              <span className="csm-plan-section-count">({items.length})</span>
                            )}
                          </div>
                          <ul className="csm-plan-items">
                            {visibleItems.map((it) => {
                              const title = (it.title || it.type || "항목").trim();
                              const content = (it.content || "").trim();
                              
                              return (
                                <li key={it.id} className="csm-plan-item">
                                  <div className="csm-plan-item-body">
                                    <div className="csm-plan-item-title">
                                      {title}
                                      <span className="csm-plan-item-tag">{it.type}</span>
                                    </div>
                                    {content && (
                                      <div className="csm-plan-item-content">{content}</div>
                                    )}
                                  </div>
                                  <button
                                    type="button"
                                    className="csm-plan-item-remove"
                                    onClick={() => removeFromLedger(it.id)}
                                    title="제거"
                                    aria-label="제거"
                                  >
                                    <FontAwesomeIcon icon={faTimes} />
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                          {shouldShowMore && (
                            <button
                              type="button"
                              className="csm-plan-section-more"
                              onClick={() => {
                                const newExpanded = new Set(expandedSections);
                                if (isExpanded) {
                                  newExpanded.delete(key);
                                } else {
                                  newExpanded.add(key);
                                }
                                setExpandedSections(newExpanded);
                              }}
                            >
                              {isExpanded ? (
                                <>
                                  <span>접기</span>
                                  <span className="csm-plan-section-more-icon">▲</span>
                                </>
                              ) : (
                                <>
                                  <span>더 보기 ({remainingCount}개)</span>
                                  <span className="csm-plan-section-more-icon">▼</span>
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
              </div>
            </div>

            {/* 5단계: 패널 하단 고정 CTA (카드 스크롤과 분리) */}
            {stepIndex === 4 && (
              <div className="csm-floating-finalize">
                <Button
                  variant="primary"
                  size="lg"
                  disabled={finalizeLoading || ledgerCards.length === 0}
                  loading={finalizeLoading}
                  loadingText="생성 중..."
                  onClick={handleFinalize}
                  title={
                    ledgerCards.length === 0
                      ? "먼저 후보 항목을 선택하세요."
                      : "선택한 항목으로 최종 생성"
                  }
                  leftIcon={(
                    <img src={sparkleIcon} alt="" aria-hidden="true" width="24" height="24" />
                  )}
                >
                  계획서 만들기
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
